// Маскот персонажа в углу экрана (подключается слотом баннеров оболочки, сам решает, показываться ли).
// Телефон: справа над нижним меню и плашкой тренировки (`--bottom-ui`); десктоп: правый нижний угол,
// над плашкой тренировки, если она есть. При прокрутке вниз уезжает за край, вверх — возвращается.
// Нажатие — всплывашка с уровнем, опытом, подсказкой и переходом в «Персонаж». Облачко объявляет
// новую тренировку (+XP) или новые достижения; закрытая всплывашка считает их увиденными.

import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { IconButton } from '@/components/ui'
import { cn } from '@/lib/cn'
import { formatDayMonth, formatNumber, pluralRu } from '@/lib/format'
import { useMuscleLabels } from '@/pages/workouts/api'

import { useAchievements, useCharacter } from './api'
import { mascotAllowedOn, mascotTip, useMascotHidden, useMascotSeen, useSeenAchievements } from './mascot/logic'
import { MascotFigure } from './mascot/MascotFigure'

const MOOD_TEXT = {
  sleepy: 'Сонный: сегодня низкий Body Battery.',
  energetic: 'Бодрый: сегодня высокая готовность.',
  sad: 'Скучает: прошла неделя без тренировок.',
}

/** Прокрутка вниз — true (спрятаться), вверх — false. */
function useScrollingDown(): boolean {
  const [down, setDown] = useState(false)
  useEffect(() => {
    let last = window.scrollY
    const onScroll = () => {
      const y = window.scrollY
      if (Math.abs(y - last) < 8) return
      setDown(y > last && y > 40)
      last = y
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])
  return down
}

export function Banner() {
  const { pathname } = useLocation()
  const [hidden] = useMascotHidden()
  if (hidden || !mascotAllowedOn(pathname)) return null
  return <Mascot key={pathname} />
}

function Mascot() {
  const query = useCharacter()
  const data = query.data && typeof query.data.level === 'number' ? query.data : undefined
  const label = useMuscleLabels()
  const [, hide] = useMascotHidden()
  const [seen, markSeen] = useMascotSeen()
  const [open, setOpen] = useState(false)
  const down = useScrollingDown()
  const root = useRef<HTMLDivElement>(null)

  const fresh = data?.last_workout && data.last_workout.id !== seen ? data.last_workout : null
  const achievements = useAchievements().data
  const [seenAchievements, markAchievements] = useSeenAchievements()
  const newAchievements = Array.isArray(achievements)
    ? achievements.filter((a) => a.unlocked_at && !seenAchievements.has(a.key))
    : []
  const close = () => {
    setOpen(false)
    if (fresh) markSeen(fresh.id)
    if (newAchievements.length) markAchievements(newAchievements.map((a) => a.key))
  }

  useEffect(() => {
    if (!open) return
    const onEvent = (event: Event) => {
      if (event instanceof KeyboardEvent ? event.key === 'Escape' : !root.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('pointerdown', onEvent)
    document.addEventListener('keydown', onEvent)
    return () => {
      document.removeEventListener('pointerdown', onEvent)
      document.removeEventListener('keydown', onEvent)
    }
  }, [open])

  const span = data ? data.next_level_xp - data.level_xp : 1
  const progress = data ? Math.min(1, Math.max(0, (data.xp - data.level_xp) / span)) : 0
  const peek = down && !open
  const mood = data?.mascot_mood ?? 'idle'

  return (
    <div
      ref={root}
      className={cn(
        'fixed right-3 z-30 bottom-[calc(var(--bottom-ui)+0.75rem)] transition-transform duration-300 lg:right-6 lg:bottom-6',
        'lg:group-has-[[data-workout-dock]]/shell:bottom-28',
        peek && 'translate-x-[70%] opacity-80',
      )}
    >
      {open ? (
        <div
          role="dialog"
          aria-label="Персонаж"
          className="mascot-pop absolute bottom-full right-0 mb-2 w-[min(18rem,calc(100vw-1.5rem))] rounded-2xl border border-border bg-surface p-4 shadow-xl shadow-black/30"
        >
          <div className="flex items-start gap-3">
            <div className="grid size-11 shrink-0 place-items-center rounded-xl bg-accent-soft text-accent">
              <span className="text-lg font-bold leading-none">{data?.level ?? '—'}</span>
              <span className="-mt-2 text-[8px] font-semibold tracking-wider">УР.</span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold">{data?.title ?? 'Персонаж'}</p>
              {data ? (
                <p className="text-xs text-muted">
                  {formatNumber(data.xp, { digits: 0 })} / {formatNumber(data.next_level_xp, { digits: 0 })} XP
                </p>
              ) : null}
            </div>
            <IconButton icon="close" label="Закрыть" size="sm" className="-mr-2 -mt-2" onClick={close} />
          </div>
          {data ? (
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surface-2">
              <div className="h-full rounded-full bg-accent" style={{ width: `${progress * 100}%` }} />
            </div>
          ) : null}
          {data?.last_workout ? (
            <p className="mt-3 text-sm">
              {data.last_workout.title}, {formatDayMonth(data.last_workout.started_at)}:{' '}
              <span className="font-semibold text-accent">+{data.last_workout.xp} XP</span>
            </p>
          ) : null}
          {newAchievements.length ? (
            <div className="mt-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted">
                {newAchievements.length === 1 ? 'Новое достижение' : 'Новые достижения'}
              </p>
              <ul className="mt-1 flex flex-col gap-1">
                {newAchievements.slice(0, 3).map((a) => (
                  <li key={a.key} className="flex items-center gap-2 text-sm">
                    <span aria-hidden className="text-lg">
                      {a.icon}
                    </span>
                    <span className="font-medium">{a.title}</span>
                  </li>
                ))}
              </ul>
              {newAchievements.length > 3 ? (
                <p className="text-xs text-muted">и ещё {newAchievements.length - 3}</p>
              ) : null}
            </div>
          ) : null}
          <p className="mt-2 rounded-lg bg-surface-2 px-3 py-2 text-sm">
            {mood !== 'idle' ? <span className="mb-1 block font-medium">{MOOD_TEXT[mood]}</span> : null}
            {data ? mascotTip(data, label) : query.isPending ? 'Загружаю…' : 'Сейчас не получилось загрузить персонажа.'}
          </p>
          <div className="mt-3 flex items-center justify-between gap-2">
            <button type="button" onClick={() => hide(true)} className="hit-area text-left text-xs text-muted hover:text-fg">
              Спрятать маскота
            </button>
            <Link
              to="/character"
              onClick={close}
              className="inline-flex min-h-10 shrink-0 items-center whitespace-nowrap rounded-lg bg-accent px-3 text-sm font-medium text-accent-fg hover:bg-accent-strong"
            >
              К персонажу →
            </Link>
          </div>
        </div>
      ) : fresh || newAchievements.length ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="mascot-pop absolute bottom-full right-1 mb-1 whitespace-nowrap rounded-full border border-accent/40 bg-surface px-3 py-1 text-sm font-semibold text-accent shadow-lg shadow-black/20"
        >
          {fresh
            ? `+${fresh.xp} XP!`
            : newAchievements.length === 1
              ? '🏆 Новое достижение!'
              : `🏆 +${newAchievements.length} ${pluralRu(newAchievements.length, ['достижение', 'достижения', 'достижений'])}`}
        </button>
      ) : null}
      <button
        type="button"
        aria-label={data ? `Персонаж: уровень ${data.level}` : 'Персонаж'}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => (open ? close() : setOpen(true))}
        className="mascot-button relative block size-14 rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent lg:size-16"
      >
        <MascotFigure
          level={data?.level}
          mood={mood === 'idle' && (fresh || newAchievements.length) ? 'cheer' : mood}
          className="size-full drop-shadow-md"
        />
        {data ? (
          <span className="absolute -left-1 -top-1 grid min-w-6 place-items-center rounded-full border-2 border-bg bg-accent px-1 text-[11px] font-bold leading-5 text-accent-fg">
            {data.level}
          </span>
        ) : null}
      </button>
    </div>
  )
}
